<template>
    <div>
        <div class="dropup">
            <button 
                v-if="!if_generate_treatment" 
                class="btn btn-secondary dropdown-toggle dropdown-toggle-no-btn" 
                type="button" 
                data-bs-toggle="dropdown" 
                aria-expanded="false" 
                :disabled="disabled"
            >
                <img src="/img/icons/avid-icons/edna.svg" class="icon me-1" alt=""> 
                {{ (asset?.source === 'generated') ? 'Refine' : 'Generate' }}
            </button>
            <button 
                v-else 
                class="btn btn-secondary dropdown-toggle dropdown-toggle-no-btn" 
                type="button" 
                data-bs-toggle="dropdown" 
                aria-expanded="false" 
                :disabled="disabled"
            >
                <img src="/img/icons/avid-icons/edna.svg" class="icon me-1" alt=""> Create a Treatment
            </button>

            <ul class="dropdown-menu dropdown-menu-primary dropdown-menu-right p-2" ref="dropdown">
                <li v-if="!if_generate_treatment">
                    <button 
                        class="dropdown-item" 
                        @click.prevent="generatePrompt('description')"
                    > 
                        {{ (asset?.source === 'generated') ? 'Regenerate' : 'Generate' }} Content
                    </button>
                </li>

                <div class="dropdown-divider"  v-if="!if_generate_treatment"></div>

                <li v-if="!if_generate_treatment">
                    <small class="dropdown-header"> 
                        {{ (asset?.source === 'generated' ) ? 'Refine' : 'Generate' }} Content Using...
                    </small>
                </li>
                <li v-else>
                    <small class="dropdown-header"> Create a Treatment Using...</small>
                </li>
                <li>
                    <a 
                        class="dropdown-item" 
                        href="#" 
                        @click.prevent="showRefinePromptModal"
                    >
                        Additional Guidance
                    </a>
                </li>
                <li>
                    <a 
                        class="dropdown-item" 
                        href="#" 
                        @click.prevent="showRegenerateModal('behavioral')"
                    >
                        Behavioral Psychology Concepts
                    </a>
                </li>
                <li v-if="completed_assets?.length">
                    <a 
                        class="dropdown-item" 
                        href="#" 
                        @click.prevent="showRegenerateModal('page')"
                    >
                        Other Assets
                    </a>
                </li>
                <li>
                    <a 
                        class="dropdown-item" 
                        href="#" 
                        @click.prevent="showRegenerateModal('framework')"
                    >
                        Writing Frameworks
                    </a>
                </li>
            </ul>
        </div>

        <!-- Regenerate Modals -->
        <div 
            v-if="asset?.id" 
            class="modal fade" 
            :id="`refine-prompt-modal-${asset.id}`" 
            tabindex="-1" 
            aria-labelledby="refinePromptModalLabel" 
            aria-hidden="true" 
            data-bs-backdrop="static" 
            data-bs-keyboard="false"
        >
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header border-0">
                        <div class="w-100">
                            <h3 v-if="!if_generate_treatment" class="mt-0">
                                {{ asset?.source === 'generated' ? 'Refine' : 'Generate' }} with Guidance
                            </h3>
                            <h3 v-else class="mt-0">Create a Treatment with Guidance</h3>
                            <p class="font-color-tertiary mb-0">
                                Describe how you want the content to be modified, and AI will
                                {{ asset?.source === 'generated' ? 'Refine' : 'generate' }} the asset based upon your directions.
                            </p>
                        </div>
                    </div>
                    <div class="modal-body pt-0 pb-2">
                        <textarea 
                            class="form-control" 
                            name="example_content" 
                            placeholder="Type or paste your guidelines..."
                            v-model="describe_changes" 
                            rows="8"
                        ></textarea>
                    </div>
                    <div class="modal-footer d-flex justify-content-end pt-1">
                        <button 
                            type="button" 
                            class="btn btn-secondary me-2" 
                            @click="closeModal(`refine-prompt-modal-${asset.id}`)"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            class="btn btn-primary" 
                            @click="generatePrompt('refine_prompt', describe_changes)"
                        >
                            <img class="icon me-2" src="/img/icons/avid-icons/edna.svg" style="width: 20px; height: 20px;" alt="">
                            {{ asset?.source === 'generated' ? 'Refine' : 'Generate' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Behavioral Psychology Concept Modal -->
        <div 
            v-if="asset?.id" 
            class="modal fade" 
            :id="`regenerate-modal-${asset.id}`" 
            tabindex="-1" 
            aria-hidden="true" 
            data-bs-backdrop="static" 
            data-bs-keyboard="false"
        >
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content p-2">
                    <div class="modal-header border-0">
                        <div class="w-100">
                            <h3 class="mt-0">{{ getModalTitle() }}</h3>
                            <p class="font-color-tertiary mb-0">{{ getModalDescription() }}</p>
                        </div>
                    </div>
                    <div class="modal-body pt-0 pb-2">
                        <div class="position-relative mb-3">
                            <div class="search-box flex-grow-1 position-relative mb-3">
                                <input 
                                    type="text" 
                                    :placeholder="`Search ${modal_type}...`" 
                                    :aria-label="`Search ${modal_type}...`" 
                                    v-model="search" 
                                    @input="filter" 
                                    autocomplete="one-time-code" 
                                    class="search-box form-control mx-0"
                                >
                                <i class="fa fa-search search-placeholder-icon" v-if="!search"></i>
                            </div>
                        </div>
                        <div class="scrollable-list">
                            <div v-if="filtered_items.length > 0">
                                <div 
                                    v-for="item in filtered_items" 
                                    :key="`item-${item.id}`" 
                                    class="card long-card mb-2" 
                                    :class="{'long-card-selected': item.id === selected_id}" 
                                    @click="selected_id = item.id"
                                >
                                    <div class="card-body long-card__body d-flex align-items-center">
                                        <div class="long-card__radio d-flex">
                                            <input 
                                                type="radio" 
                                                :id="`item-${item.id}`" 
                                                name="selected_item" 
                                                :value="item.id" 
                                                v-model="selected_id" 
                                                class="form-radio-input"
                                             aria-label="Radio option">
                                        </div>
                                        <div class="long-card__content d-flex flex-column gap-1">
                                            <h4 class="mb-0">{{ item.name }}</h4>
                                            <p class="mb-0 font-color-tertiary" v-if="item.description">
                                                <template v-if="item.description.length > 120">
                                                    <span v-if="!expanded_items.has(item.id)">
                                                        {{ truncateText(item.description) }}...
                                                        <a href="#" class="text-primary" @click.prevent="toggleExpand(item.id)">more</a>
                                                    </span>
                                                    <span v-else>
                                                        {{ item.description }}
                                                    </span>
                                                </template>
                                                <span v-else>{{ item.description }}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-center py-3">
                                <p v-if="modal_type === 'behavioral'" class="mb-0">No concepts found matching your search.</p>
                                <p v-if="modal_type === 'framework'" class="mb-0">No frameworks found matching your search.</p>
                                <p v-if="modal_type === 'page'" class="mb-0">No pages found matching your search.</p>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer d-flex justify-content-end pt-1">
                        <button 
                            type="button" 
                            class="btn btn-secondary me-2" 
                            @click="asset?.id ? closeModal(`regenerate-modal-${asset.id}`) : null"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            class="btn btn-primary" 
                            @click="generatePrompt(modal_type)" 
                            :disabled="!selected_id"
                        >
                            <img class="icon me-2" src="/img/icons/avid-icons/edna.svg" style="width: 20px; height: 20px;" alt="">
                            {{ (asset?.source === 'generated' ) ? 'Refine' : 'Generate' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { usePlaybookStore } from '../../stores/playbook';
import statsigService from '../../services/statsig';

export default defineComponent({
    name: 'RegenerateButton',
    emits: ['asset-generating', 'asset-generated', 'treatment-generated'],
    props: {
        client: {
            type: Object,
            required: true
        },
        asset: {
            type: Object,
            required: true
        },
        concepts: {
            type: Array,
            required: false,
            default: () => []
        },
        if_generate_treatment: {
            type: Boolean,
            required: false,
            default: false
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    setup() {
        const playbookStore = usePlaybookStore();
        return { playbookStore };
    },
    data() {
        return {
            local_asset: this.asset,
            modal_type: null,
            search: '',
            selected_id: null,
            filtered_items: [],
            describe_changes: '',
            expanded_items: new Set(),
            completed_assets: []
        }
    },
    computed: {
        playbook() {
            return this.playbookStore.getSelectedPlaybook;
        }
    },
    mounted() {
        this.$nextTick(() => {
            if (this.asset?.id) {
                const refineModal = document.getElementById(`refine-prompt-modal-${this.asset.id}`);
                const regenerateModal = document.getElementById(`regenerate-modal-${this.asset.id}`);

                if (refineModal) {
                    document.body.appendChild(refineModal);
                }

                if (regenerateModal) {
                    document.body.appendChild(regenerateModal);
                }
            }

            // Update completed assets when component is mounted
            this.updateCompletedAssets();
        });
    },
    beforeUnmount() {
        if (this.asset?.id) {
            const refineModal = document.getElementById(`refine-prompt-modal-${this.asset.id}`);
            const regenerateModal = document.getElementById(`regenerate-modal-${this.asset.id}`);

            if (refineModal?.parentNode === document.body) {
                document.body.removeChild(refineModal);
            }

            if (regenerateModal?.parentNode === document.body) {
                document.body.removeChild(regenerateModal);
            }
        }
    },
    watch: {
        playbook: {
            handler() {
                this.updateCompletedAssets();
            },
            immediate: true,
            deep: true
        },
        asset: {
            handler(newVal) {
                // keep the local copy of the asset in sync with the latest prop
                this.local_asset = newVal;
                this.updateCompletedAssets();
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        truncateText(text) {
            if (text.length <= 120) return text;

            // Find the last space before 120 characters
            const truncated = text.substring(0, 120);
            const lastSpace = truncated.lastIndexOf(' ');

            // If we found a space, use that as our cutoff point
            return lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated;
        },
        toggleExpand(id) {
            const newSet = new Set(this.expanded_items);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            this.expanded_items = newSet;
        },
        showModal(type) {
            this.modal_type = type;
            this.resetItems();
            this.resetScroll();
        },
        showRefinePromptModal() {
            if (this.asset?.id) {
                const modalElement = document.getElementById(`refine-prompt-modal-${this.asset.id}`);
                if (modalElement) {
                    // Dispose any existing modal instance first
                    const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                    if (existingModal) {
                        existingModal.dispose();
                    }
                    
                    const modal = new window.bootstrap.Modal(modalElement, {
                        backdrop: 'static',
                        keyboard: false
                    });
                    modal.show();
                }
            }
        },
        showRegenerateModal(type) {
            this.showModal(type);
            if (this.asset?.id) {
                const modalElement = document.getElementById(`regenerate-modal-${this.asset.id}`);
                if (modalElement) {
                    // Dispose any existing modal instance first
                    const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                    if (existingModal) {
                        existingModal.dispose();
                    }
                    
                    const modal = new window.bootstrap.Modal(modalElement, {
                        backdrop: 'static',
                        keyboard: false
                    });
                    modal.show();
                }
            }
        },
        resetScroll() {
            const scrollableList = document.querySelector('.scrollable-list');
            if (scrollableList) {
                scrollableList.scrollTop = 0;
            }
        },
        resetItems() {
            this.filtered_items = [];
            if (this.modal_type === 'behavioral' || this.modal_type === 'framework') {
                for (let concept of this.concepts) {
                    if (concept.type === this.modal_type) {
                        this.filtered_items.push({
                            id: concept.id,
                            name: concept.name,
                            description: concept.definition
                        });
                    }
                }
            } else if (this.modal_type === 'page') {
                for (let asset of this.completed_assets) {
                    this.filtered_items.push({
                        id: asset.id,
                        name: asset.asset_type.name.substring(0, asset.asset_type.name.length - 1) +
                            ": " + asset.name,
                        description: asset.description
                    });
                }
            }
        },
        getModalTitle() {
            let title;
            if (!this.if_generate_treatment) {
                title = (this.asset?.source === 'generated' ? 'Refine' : 'Generate') + ' Using ';
            } else {
                title = 'Create a Treatment Using ';
            }

            if (this.modal_type === 'behavioral') {
                return title + 'a Behavioral Psychology Concept';
            } else if (this.modal_type === 'framework') {
                return title + 'a Writing Framework';
            } else if (this.modal_type === 'page') {
                return title + 'an Existing Asset';
            }
        },
        getModalDescription() {
            if (this.modal_type === 'behavioral') {
                return 'Behavioral psychology concepts are strategies and principles derived from psychology and consumer behavior analysis to craft engaging, personalized, and effective content.';
            } else if (this.modal_type === 'framework') {
                return 'These frameworks will help you test different writing styles to find the one that best matches the ideal tone and communication style for your audience.';
            } else if (this.modal_type === 'page') {
                return 'Use content from other completed assets as a reference for generating this asset.';
            }
        },
        generatePrompt(option, content = null) {
            if (!this.asset) return;

            if (this.if_generate_treatment) {
                const asset_input = [];
                this.local_asset.inputs.forEach(input_field => {
                    if (input_field.type === "wysiwyg") {
                        const formatted_value = input_field.value ? input_field.value
                            .replace('data-bs-toggle="modal" ', '')
                            .replace(`data-bs-target="#insert-image-modal-${this.local_asset.id}" `, '')
                            .replace(`data-bs-target="#insert-button-modal-${this.local_asset.id}" `, '') : '';
                        asset_input.push({
                            placeholder: input_field.placeholder,
                            value: formatted_value
                        });
                    } else if (input_field.type === "image" && Array.isArray(input_field.value) && input_field.value.length === 1 && input_field.value[0] === null) {
                        // If image array is empty, empty image array would be an array has only one element and the only element is null
                        asset_input.push({
                            placeholder: input_field.placeholder,
                            value: null
                        });
                    } else {
                        asset_input.push({
                            placeholder: input_field.placeholder,
                            value: input_field.value
                        });
                    }
                });
                this.local_asset.inputs = asset_input;

                // create a new playbook
                const local_playbook = { ...this.playbook };
                local_playbook.name = this.local_asset.name;
                local_playbook.description = this.local_asset.description;
                local_playbook.playbook_type_id = 0;
                
                // Simple asset data for experiment variants
                const assetData = {
                    name: this.local_asset.name,
                    description: this.local_asset.description,
                    playbook_asset_type_id: this.local_asset.playbook_asset_type_id,
                    inputs: this.local_asset.inputs,
                    status: 'draft',
                    is_active: true
                };
                
                local_playbook.assets = [assetData];
                local_playbook.variant = {
                    option_selected: option,
                    content: content != null ? content : this.selected_id
                };

                window.axios.post(`/api/playbooks/${this.client.url}/experiment_variant`, local_playbook)
                    .then(response => {
                        // Emit event with playbook data instead of redirecting
                        this.$emit('treatment-generated', response.data.playbook);
                    });
            } else {
                // Track copy regeneration from edit touchpoint
                if (statsigService.getClient()) {
                    statsigService.logEvent('playbook_copy_regenerated', null, {
                        playbook_id: this.playbook?.id,
                        playbook_type_name: this.playbook?.playbook_type?.name,
                        asset_id: this.asset?.id,
                        source: 'edit_touchpoint'
                    });
                }
                
                const request_body = { ...this.asset };
                request_body.option_selected = option;
                request_body.content = content != null ? content : this.selected_id;

                // Update the asset's source to 'generating' immediately
                this.local_asset.status = 'generating';
                this.$emit('asset-generating', this.local_asset);

                window.axios.put('/api/playbooks/'+this.client.url+'/generate/'+this.asset.id, request_body).then(response => {
                    let asset = response.data.asset;
                    
                    asset.name = request_body.name;
                    asset.description = request_body.description;
                    asset.launch_date = request_body.launch_date;
                    asset.destination_asset_id = request_body.destination_asset_id;
                    asset.segment_id = request_body.segment_id;
                    asset.segment_type = request_body.segment_type;
                    asset.status = request_body.status;
                    
                    this.$emit('asset-generated', asset);
                });
                // Close all modals after generating content
                this.closeModal(`refine-prompt-modal-${this.asset.id}`);
                this.closeModal(`regenerate-modal-${this.asset.id}`);
            }
        },
        closeModal(id) {
            const myModalEl = document.getElementById(id);
            if (myModalEl) {
                const modal = bootstrap.Modal.getInstance(myModalEl);
                if (modal) {
                    this.resetScroll();
                    modal.hide();
                }
            }
            this.describe_changes = '';

            // Clear behavioral concept modal data if that's the modal being closed
            if (id.includes('regenerate-modal')) {
                this.selected_id = null;
                this.search = '';
            }
        },
        filter() {
            this.resetItems();
            if (this.search) {
                const searchTerm = this.search.toLowerCase();
                this.filtered_items = this.filtered_items.filter(item =>
                    item.name.toLowerCase().includes(searchTerm) ||
                    (item.description && item.description.toLowerCase().includes(searchTerm))
                );
            }
        },
        isDraftAsset(asset) {
            // A touchpoint is in draft mode if it's missing any required field
            // Uses the same logic as TouchpointsGridView.vue isDraftTouchpoint
            if (!asset || !asset.asset_type || !asset.asset_type.inputs || !asset.inputs) {
                return true;
            }

            // Get required inputs (inputs where optional is false or not set)
            const requiredInputs = asset.asset_type.inputs.filter(input => !input.optional);

            // Helper function to check if a value is empty
            const isEmpty = (value) => {
                if (value === undefined || value === null) return true;
                if (typeof value === 'string') return value.trim() === '';
                if (Array.isArray(value)) {
                    // Empty array is empty
                    if (value.length === 0) return true;
                    // Array with only null/undefined/empty values is considered empty
                    return value.every(item => {
                        if (item === null || item === undefined) return true;
                        if (typeof item === 'string') return item.trim() === '';
                        if (Array.isArray(item)) return isEmpty(item);
                        // For objects, check if they're empty (no meaningful content)
                        if (typeof item === 'object') {
                            // If it's an object, check if it has any non-empty properties
                            return Object.keys(item).length === 0 || 
                                   Object.values(item).every(val => isEmpty(val));
                        }
                        return false;
                    });
                }
                return false;
            };

            // Helper function to check group inputs
            const checkGroupInputs = (groupInput, groupItems) => {
                if (!groupItems || !Array.isArray(groupItems)) return true;

                // Get all required inputs for this group
                const groupRequiredInputs = asset.asset_type.inputs.filter(
                    input => input.group_id === groupInput.id && !input.optional
                );

                // Check each item in the group
                for (const item of groupItems) {
                    // Skip null or undefined items
                    if (!item) continue;

                    for (const requiredInput of groupRequiredInputs) {
                        // Skip if requiredInput is null or doesn't have a placeholder
                        if (!requiredInput || !requiredInput.placeholder) continue;

                        // Safely access the value using the placeholder
                        const value = item[requiredInput.placeholder];
                        if (isEmpty(value)) {
                            return true; // Missing required field in group
                        }
                    }
                }

                return false; // All required fields in group are present
            };

            // Check each required input
            for (const requiredInput of requiredInputs) {
                // Skip if requiredInput is null or doesn't have a placeholder
                if (!requiredInput || !requiredInput.placeholder) continue;

                // Skip inputs that belong to a group (they're checked within their group)
                if (requiredInput.group_id) {
                    continue;
                }

                // Find the corresponding input value in asset.inputs
                const input = Array.isArray(asset.inputs) 
                    ? asset.inputs.find(input => input && input.placeholder === requiredInput.placeholder)
                    : null;

                // If input is missing, it's a draft
                if (!input) {
                    // Exclude citations and link placeholders from required check
                    if (requiredInput.placeholder !== 'citations' && requiredInput.placeholder !== 'link') {
                        return true;
                    }
                    continue;
                }

                // Handle group inputs
                if (requiredInput.type === 'group') {
                    if (isEmpty(input.value) || checkGroupInputs(requiredInput, input.value)) {
                        return true; // Missing required group or required fields within group
                    }
                }
                // Handle regular inputs (not in a group)
                else if (isEmpty(input.value)) {
                    // Exclude citations and link placeholders from required check
                    if (requiredInput.placeholder !== 'citations' && requiredInput.placeholder !== 'link') {
                        return true; // Missing required field
                    }
                }
            }

            // All required fields are present
            return false;
        },
        updateCompletedAssets() {
            // Reset the array
            this.completed_assets = [];

            if (!this.playbook?.assets || !this.asset?.id) {
                return;
            }

            // Filter assets to find completed ones
            // An asset is complete if it has a source (not null) and is not a draft
            // This matches the logic in TouchpointsGridView.vue getTouchpointStatus
            this.completed_assets = this.playbook.assets.filter(playbookAsset => {
                // Skip the current asset
                if (playbookAsset.id === this.asset.id) {
                    return false;
                }

                // Asset is complete if it has a source and is not a draft
                // This matches the "Incomplete" status check: source == null || isDraftTouchpoint
                return playbookAsset.source != null && !this.isDraftAsset(playbookAsset);
            });
        },
    },
});
</script>
